/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Popover,
  makeStyles,
  Divider,
  IconButton,
  Paper,
  Select,
  MenuItem,
  InputBase,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { FaCalendar } from "react-icons/fa";
import { GoSearch } from "react-icons/go";
import _ from "lodash";
import { getCycleTypeId } from "lib/utils/cycles";
import { List, AutoSizer } from "react-virtualized";
import { useLocation, useRouteMatch } from "react-router-dom";
import { primaryColor } from "resources/constants";
import { useDebounce } from "lib/react/customHooks";
import ScenarioOptionComponent from "./ScenarioOptionComponent";

const useStyles = makeStyles((theme) => ({
  selectDivider: {
    backgroundColor: "#e2e2e2",
  },
  scenarioSelectorPopover: {
    marginLeft: "10px",
    height: "100%",
    maxHeight: "600px",
    width: "600px",
  },
  starredScenario: {
    borderLeft: "5px solid #e86861",
  },
  scenarioSelectorList: {
    "&:focus": {
      outline: "none",
    },
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },

    /* Handle */
    "&::-webkit-scrollbar-thumb": {
      background: "#bbb",
      borderRadius: "5px",
      padding: "1px",
    },

    /* Handle on hover */
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#999",
    },
  },
  root: {
    padding: "3px",
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    flexGrow: "1",
    backgroundColor: primaryColor,
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
    color: "#fff",
    fontSize: "0.875rem",
  },
  iconButton: {
    padding: 10,
    color: "#fff",
  },
  divider: {
    height: 28,
    margin: 4,
    backgroundColor: "white",
  },
  searchButton: {
    "&:hover": {
      cursor: "auto",
    },
  },
  cycleSelect: {
    margin: "0px 10px 0px 10px",
    color: "#fff",
    "& > div:first-child": {
      paddingRight: "5px",
    },
    minWidth: "175px",
    fontSize: "0.875rem",
  },
  noScenariosFoundText: {
    padding: "10px 10px 10px 10px",
  },
}));

export default function ScenarioSelectorComponent({
  setScenarioSelectorAnchorEl,
  scenarioSelectorAnchorEl,
}) {
  const classes = useStyles();
  const location = useLocation();
  const { url } = useRouteMatch();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const scenariosSlice = useSelector((state) => state.scenarios);
  const metadataSlice = useSelector((state) => state.metadata);
  const cycleSlice = useSelector((state) => state.cycle);
  const openScenarioSelector = Boolean(scenarioSelectorAnchorEl);
  const scenarioSelectorId = openScenarioSelector
    ? "scenario-selector"
    : undefined;
  const [searchTerm, setSearchTerm] = React.useState("");
  const [cycleList, setCycleList] = React.useState([]);
  const [selectedCycle, setSelectedCycle] = React.useState(
    scenariosSlice.selectedScenario.scenario.cycle_id
  );
  const [visibleScenarios, setVisibleScenarios] = React.useState(
    scenariosSlice.totalsByScenarioId
  );

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const applyScenarioFilters = () => {
    const filteredScenarios = _.filter(
      scenariosSlice.totalsByScenarioId,
      (scenarioTotal) => {
        return (
          scenarioTotal.scenario.cycle.id === selectedCycle &&
          _.some(scenarioTotal.scenario, (value) => {
            return (
              typeof value === "string" &&
              value.toLowerCase().includes(searchTerm.toLowerCase())
            );
          })
        );
      }
    );
    return filteredScenarios;
  };

  const handleCycleChange = (event) => {
    setSelectedCycle(event.target.value);
  };

  const handleScenarioSelectorClose = () => {
    setScenarioSelectorAnchorEl(null);
    setSearchTerm("");
  };

  const handleCycleType = (id) => {
    const cycleTypeList = _.filter(cycleSlice.list, (cycle) => {
      if (getCycleTypeId("Pricing", cycleSlice)[0].id === id) {
        return (
          cycle.cycle_type_id === id ||
          cycle.cycle_type_id ===
            getCycleTypeId("Pricing 2.0", cycleSlice)[0].id
        );
      }
      if (getCycleTypeId("Trade Promotions", cycleSlice)[0].id === id) {
        return (
          cycle.cycle_type_id === id ||
          cycle.cycle_type_id ===
            getCycleTypeId("Daily Trade Promotions", cycleSlice)[0].id
        );
      }

      return cycle.cycle_type_id === id;
    });
    setCycleList(cycleTypeList);
    return cycleTypeList;
  };

  useEffect(() => {
    switch (location.pathname) {
      case `${url}/pricing`:
        handleCycleType(getCycleTypeId("Pricing", cycleSlice)[0].id);
        break;
      default:
        handleCycleType(getCycleTypeId("Trade Promotions", cycleSlice)[0].id);
        break;
    }
  }, [location.pathname]);

  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setVisibleScenarios(applyScenarioFilters);
  }, [searchTerm, selectedCycle, scenariosSlice.totalsByScenarioId]);
  return (
    <Popover
      id={scenarioSelectorId}
      open={openScenarioSelector}
      onClose={handleScenarioSelectorClose}
      anchorEl={scenarioSelectorAnchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      classes={{ paper: classes.scenarioSelectorPopover }}
    >
      <>
        <form
          className={classes.root}
          onSubmit={(event) => event.preventDefault()}
          noValidate
          autoComplete="off"
        >
          <Paper className={classes.root}>
            <InputBase
              className={classes.input}
              placeholder="Search scenarios"
              inputProps={{ "aria-label": "search scenarios" }}
              value={searchTerm}
              onChange={handleInputChange}
            />
            <IconButton
              type="button"
              disableRipple
              disableFocusRipple
              className={`${classes.iconButton} ${classes.searchButton}`}
              aria-label="search"
            >
              <GoSearch size={18} />
            </IconButton>
            <Divider className={classes.divider} orientation="vertical" />
            <Select
              disableUnderline
              labelId="cycle-select"
              id="cycle-select"
              label="cycle"
              autoWidth
              IconComponent={() => <FaCalendar size={22} />}
              onChange={handleCycleChange}
              value={selectedCycle}
              className={classes.cycleSelect}
            >
              {cycleList.map((cycle) => {
                return (
                  <MenuItem key={cycle.id} value={cycle.id}>
                    {cycle.label}
                  </MenuItem>
                );
              })}
            </Select>
          </Paper>
        </form>
        {visibleScenarios.length > 0 ? (
          <AutoSizer>
            {({ width, height }) => {
              return (
                <List
                  className={classes.scenarioSelectorList}
                  width={width}
                  height={height - 50} // We have to substract the height of the list header
                  rowHeight={isSmallScreen ? 275 : 165}
                  rowCount={visibleScenarios.length}
                  rowRenderer={({ index, key, style }) => {
                    return (
                      <div
                        key={key}
                        style={style}
                        className={
                          visibleScenarios[index].scenario.is_starred &&
                          classes.starredScenario
                        }
                      >
                        <ScenarioOptionComponent
                          scenarioTotal={visibleScenarios[index]}
                          visibleScenarios={visibleScenarios}
                          referenceScenarioTotal={_.find(
                            scenariosSlice.totalsByScenarioId,
                            (scenarioTotal) =>
                              scenarioTotal.scenario.scenario_type_id === 1 &&
                              scenarioTotal.scenario.cycle_id ===
                                visibleScenarios[0].scenario.cycle_id
                          )}
                          selectedScenario={scenariosSlice.selectedScenario}
                          metadata={metadataSlice}
                          closeParentPopover={handleScenarioSelectorClose}
                        />
                        <Divider className={classes.selectDivider} />
                      </div>
                    );
                  }}
                />
              );
            }}
          </AutoSizer>
        ) : (
          <Typography variant="body2" className={classes.noScenariosFoundText}>
            No custom scenarios where found with the specified criteria.
          </Typography>
        )}
      </>
    </Popover>
  );
}
